
import React from "react";
import { useReportsData } from "@/hooks/reports";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  Legend, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TimeRangeSelector } from "./TimeRangeSelector";

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658'];

export function CategoryPopularityReport() {
  const { loading, getCategoryPopularity, timeRange, setTimeRange } = useReportsData();
  
  const categoryData = getCategoryPopularity();
  
  // Sort data by order count
  const sortedByOrders = [...categoryData].sort((a, b) => b.orders - a.orders);
  const sortedByRevenue = [...categoryData].sort((a, b) => b.revenue - a.revenue);
  
  // Calculate totals
  const totalOrders = categoryData.reduce((sum, cat) => sum + cat.orders, 0);
  const totalRevenue = categoryData.reduce((sum, cat) => sum + cat.revenue, 0);
  
  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Category Popularity Analysis</h3>
        <TimeRangeSelector value={timeRange} onChange={setTimeRange} />
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-4">
        <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top Product by Orders</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <>
                <div className="text-2xl font-bold">{sortedByOrders[0]?.name || "N/A"}</div>
                <p className="text-sm text-muted-foreground">
                  {sortedByOrders[0]?.orders || 0} orders ({sortedByOrders[0]?.orders && totalOrders ? 
                    Math.round((sortedByOrders[0].orders / totalOrders) * 100) : 0}% of total)
                </p>
              </>
            ) : (
              <p>No product data available</p>
            )}
          </CardContent>
        </Card>
        
        {/* <Card className="shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Top Product by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            {categoryData.length > 0 ? (
              <>
                <div className="text-2xl font-bold">{sortedByRevenue[0]?.name || "N/A"}</div>
                <p className="text-sm text-muted-foreground">
                  ${sortedByRevenue[0]?.revenue.toLocaleString() || 0} ({sortedByRevenue[0]?.revenue && totalRevenue ? 
                    Math.round((sortedByRevenue[0].revenue / totalRevenue) * 100) : 0}% of total)
                </p>
              </>
            ) : (
              <p>No product data available</p>
            )}
          </CardContent>
        </Card> */}
      </div>
      
      <div className="grid grid-cols-1 lg:grid-cols-1 gap-6">
        <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Products by Order Count</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p>Loading data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="orders"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [value, 'Orders']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card>
        
        {/* <Card className="shadow-sm">
          <CardHeader>
            <CardTitle className="text-base">Products by Revenue</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center items-center h-64">
                <p>Loading data...</p>
              </div>
            ) : (
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={categoryData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="revenue"
                    nameKey="name"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  >
                    {categoryData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip formatter={(value) => [`$${value}`, 'Revenue']} />
                  <Legend />
                </PieChart>
              </ResponsiveContainer>
            )}
          </CardContent>
        </Card> */}
      </div>
      
      <Card className="shadow-sm">
        <CardHeader>
          <CardTitle className="text-base">Product Performance Table</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Product Name</TableHead>
                  <TableHead>Total Orders</TableHead>
                  <TableHead>% of Orders</TableHead>
                  {/* <TableHead>Total Revenue</TableHead>
                  <TableHead>% of Revenue</TableHead>
                  <TableHead>Avg. Revenue/Order</TableHead> */}
                </TableRow>
              </TableHeader>
              <TableBody>
                {categoryData.map((category, index) => (
                  <TableRow key={index}>
                    <TableCell className="font-medium">{category.name}</TableCell>
                    <TableCell>{category.orders}</TableCell>
                    <TableCell>{totalOrders ? Math.round((category.orders / totalOrders) * 100) : 0}%</TableCell>
                    {/* <TableCell>${category.revenue.toLocaleString()}</TableCell>
                    <TableCell>{totalRevenue ? Math.round((category.revenue / totalRevenue) * 100) : 0}%</TableCell>
                    <TableCell>${category.orders ? (category.revenue / category.orders).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : 0}</TableCell> */}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
